<template>
	<div class="c-global-checkout__form-section c-global-checkout__form-section--phone" v-if="!isHidden">
		<div>
			<label :for="leftFieldId" :data-field-name="leftFieldName" :id="('scrolltarget-' + leftFieldName)">{{
				leftLabel
			}}</label>
			<label :for="rightFieldId" :data-field-name="rightFieldName" :id="('scrolltarget-' + rightFieldName)">{{
				rightLabel
			}}</label>
		</div>
		<div>
			<input
				:id="leftFieldId"
				type="tel"
				:name="leftName"
				:data-field-name="leftFieldName"
				v-model="formData[leftFieldName]"
				:placeholder="leftPlaceholderText"
				:aria-errormessage="errorId"
				:required="required"
				:maxLength="leftMaxLength"
				:class="{
					'show-error': hasError(leftFieldName),
				}"
				:aria-invalid="hasError(leftFieldName) || null"
				:tabindex="0"
				autocomplete="off"
				@blur="onBlur"
				@focus="onFocus"
			/>
			<input
				:id="rightFieldId"
				type="tel"
				:name="rightName"
				:data-field-name="rightFieldName"
				v-model="formData[rightFieldName]"
				:placeholder="rightPlaceholderText"
				:aria-errormessage="errorId"
				:required="required"
				:maxLength="rightMaxLength"
				:class="{
					'show-error': hasError(rightFieldName),
				}"
				:aria-invalid="hasError(rightFieldName) || null"
				:tabindex="0"
				@blur="onBlur"
				@focus="onFocus"
			/>
		</div>
		<div :id="errorId" :data-tracking-name="trackingName">
			<div class="error-box" :data-field-name="leftFieldName">
				<span
					class="error show-error validation-error"
					v-if="hasErrorType(leftFieldName, 'requiredError')"
					>{{ leftRequiredErrorText }}</span
				>
				<span
					class="error show-error max-length-error"
					v-if="hasErrorType(leftFieldName, 'maxLengthError')"
					>{{ leftMaxLengthErrorText }}</span
				>
				<span
					class="error format-error show-error"
					v-if="hasErrorType(leftFieldName, 'formatError')"
					>{{ leftFormatErrorText }}</span
				>
				<span
					class="error api-error show-error"
					v-if="hasErrorType(leftFieldName, 'apiValidationError')"
					>{{ leftApiValidationError }}</span
				>
			</div>
			<div class="error-box" :data-field-name="rightFieldName">
				<span
					class="error show-error validation-error"
					v-if="hasErrorType(rightFieldName, 'requiredError')"
					>{{ rightRequiredErrorText }}</span
				>
				<span
					class="error show-error max-length-error"
					v-if="hasErrorType(rightFieldName, 'maxLengthError')"
					>{{ rightMaxLengthErrorText }}</span
				>
				<span
					class="error format-error show-error"
					v-if="hasErrorType(rightFieldName, 'formatError')"
					>{{ rightFormatErrorText }}</span
				>
				<span
					class="error api-error show-error"
					v-if="hasErrorType(rightFieldName, 'apiValidationError')"
					>{{ rightApiValidationError }}</span
				>
			</div>
		</div>
	</div>
</template>

<script>
import store from "shared/store";
import { validateFieldState } from "multisite/services/checkoutHelperService.js";
import { addressTypes } from 'shared/settings/constants';

export default {
	name: "phone-input-field",
	store,
	props: {
		formData: { type: Object, required: true },
		submitted: Boolean,
		errors: {
			type: Object,
			default: {},
		},
		/** @type {function(fieldName: string, state: Object)} */
		updateFieldError: {
			type: Function,
		},
		leftFieldId: {
			type: String,
			required: true,
		},
		leftLabel: {
			type: String,
			default: null,
		},
		leftName: {
			type: String,
			default: null,
		},
		leftFieldName: {
			type: String,
			default: null,
		},
		leftMaxLength: {
			type: Number,
			default: 0,
		},
		leftMaxLengthErrorText: {
			type: String,
			default: "",
		},
		leftRequiredErrorText: {
			type: String,
			default: "",
		},
		leftPlaceholderText: {
			type: String,
			default: "",
		},
		leftValidatePattern: {
			default: null,
		},
		leftFormatErrorText: {
			type: String,
			default: "",
		},
		rightPlaceholderText: {
			type: String,
			default: "",
		},
		rightFieldId: {
			type: String,
			required: true,
		},
		rightLabel: {
			type: String,
			default: null,
		},
		rightName: {
			type: String,
			default: null,
		},
		rightFieldName: {
			type: String,
			default: null,
		},
		required: {
			type: Boolean,
			default: false,
		},
		rightMaxLength: {
			type: Number,
			default: 0,
		},
		rightMaxLengthErrorText: {
			type: String,
			default: "",
		},
		rightRequiredErrorText: {
			type: String,
			default: "",
		},
		rightValidatePattern: {
			default: null,
		},
		rightFormatErrorText: {
			type: String,
			default: "",
		},
		defaultInternationalCallingCode: {
			type: String,
			default: "",
		},
		availableForAlternativeAddresses: {
			type: Boolean,
			default: false
		},
		addressType: {
			type: String,
			default: 'shippingAddress'
		},
	},
	data() {
		return {
			errorId: `error_${this.rightFieldId || this.rightFieldName}`,
			trackingName: this.rightLabel,
			hasBlur: {
				internationalCallingCode: false,
				phoneNumber: false,
			},
		};
	},
	computed: {
		leftApiValidationError() {
			return this.errors[this.leftFieldName]?.apiValidationError || "";
		},
		rightApiValidationError() {
			return this.errors[this.rightFieldName]?.apiValidationError || "";
		},
		isHidden() {
			return this.addressType !== addressTypes.SHIPPING_ADDRESS && !this.availableForAlternativeAddresses;
		}
	},
	watch: {
		formData: {
			handler() {
				this.validateFields();
			},
			deep: true,
		},
	},
	created() {
		if (!this.formData[this.leftFieldName] && !!this.defaultInternationalCallingCode) {
			this.formData[this.leftFieldName] = this.defaultInternationalCallingCode;
		}
	},
	mounted() {
		this.validateFields();
	},
	methods: {
		hasErrorType(item, type) {
			return (this.hasBlur[item] || this.submitted) && this.errors[item]
				? this.errors[item][type]
				: false;
		},
		hasError(item) {
			return this.hasBlur[item] || this.submitted
				? this.errors[item] && !this.errors[item].isValid
				: false;
		},
		validateFields() {
			this.updateFieldError(
				this.leftFieldName,
				// Is the calling code valid
				validateFieldState(this.formData[this.leftFieldName], {
					required: this.leftRequired || this.required,
					maxLength: this.leftMaxLength,
					validatePattern: this.leftValidatePattern,
				})
			);
			this.updateFieldError(
				this.rightFieldName,
				// Is the number itself valid
				validateFieldState(this.formData[this.rightFieldName], {
					required: this.rightRequired || this.required,
					maxLength: this.rightMaxLength,
					validatePattern: this.rightValidatePattern,
				})
			);
		},
		onBlur(event) {
			this.hasBlur[event.target.dataset.fieldName] = true;
			this.$emit("blur", event);
		},
		onFocus(event) {
			this.$emit("focus", event);
		},
	},
};
</script>
